<script lang="ts">
	import { getTranslate } from "@utils/getTranslate.js";
	import { openToast } from "@utils/openToast.js";
	import thumbsUpIcon from "@core/assets/icons/thumbs-up.svg?raw";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import { CartModalKind } from "@core/schema/CartModalKind.js";
	import { EconomicSubjectVatType } from "@core/schema/EconomicSubjectVatType.js";
	import type { OrderProductAttribute } from "@core/schema/OrderProductInfo.js";
	import type { ProductOfVariantForCustomer, ProductVariantForCustomer } from "@core/schema/Product.js";
	import { ProductVariantType } from "@core/schema/ProductVariantType.js";
	import { getCartPath } from "@core/schema/paths/getCartPath.js";
	import { getProductStockAmount } from "@core/schema/utils/getProductStockAmount.js";
	import { isSameOrderProduct } from "@core/schema/utils/isSameOrderProduct.js";
	import { transformToOrderProduct } from "@core/schema/utils/transformToOrderProduct.js";
	import { tw } from "@core/utils/svelte/tw.js";
	import { Cart } from "../cart/Cart.svelte.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { selectedCurrency } from "../state/selectedCurrency.svelte.js";
	import { selectedDestinationCountry } from "../state/selectedDestinationCountry.svelte.js";
	import translations from "./ProductCardCartOptions.translations.json" with { type: "json" };
	import Button from "./common/Button.svelte";
	import { ButtonSize } from "./common/ButtonSize.js";
	import CartQuantityStepper from "./common/CartQuantityStepper.svelte";
	import Formatted from "./common/Formatted.svelte";
	import Modal from "./common/Modal.svelte";
	import Dropdown from "./form/Dropdown.svelte";
	import { maxCartAdditionAmount } from "./maxCartAdditionAmount.js";

	const {
		max: initalMax,
		oncountchange,
		onvariantidchange,
		product,
		attributes,
		showZeroCount = false,
		shrinkOnMobile = false,
		showToasts,
		disabled: disabledAttribute = false,
		locked = false,
	}: {
		max?: number;
		oncountchange?(this: void, count: number): void;
		onvariantidchange?(this: void, id: ProductVariantForCustomer["id"]): void;
		product: ProductOfVariantForCustomer;
		attributes: OrderProductAttribute[];
		showZeroCount?: boolean;
		shrinkOnMobile?: boolean;
		showToasts: boolean;
		disabled?: boolean;
		/** Products the customer cannot buy yet — adding opens the Father’s Family Club modal instead. */
		locked?: boolean;
	} = $props();

	const translate = getTranslate(translations);
	const { tenant, subcategories } = getGlobalTemplateProps();
	let showConfirmRemovalModal = $state(false);

	const orderProduct = $derived.by(() => {
		if (selectedCurrency.current === undefined || selectedDestinationCountry.current === null) {
			return null;
		}

		const newOrderProduct = transformToOrderProduct(
			product,
			attributes,
			{
				destinationCountry: selectedDestinationCountry.current,
				currency: selectedCurrency.current,
				subcategories,
				vatType: Cart.vatType ?? EconomicSubjectVatType.NotVatSubject,
			},
			1,
		);

		return Cart.orderProducts.find((product) => isSameOrderProduct(product, newOrderProduct));
	});
	const count = $derived(orderProduct?.quantity ?? 0);

	const max = $derived(Math.min(initalMax ?? Infinity, maxCartAdditionAmount));
	const disabled = $derived(disabledAttribute || count >= max);

	function increment(): void {
		if (locked) {
			Cart.openCartModal(CartModalKind.FfcMembersOnly, {});
			return;
		}

		if (!disabled) {
			Cart.addProduct(product, attributes, 1, undefined, (newlyAdded) => {
				if (showToasts) {
					openToast({
						group: "cart",
						icon: thumbsUpIcon,
						title: translate(newlyAdded ? "addedToCart" : "againAdded", { name: product.name[tenant.locale] }),
						onclick(): void {
							location.assign(getCartPath(tenant));
						},
					});
				}
			});
		}
	}

	function decrement(force = false): void {
		if (count === 1 && !force) {
			showConfirmRemovalModal = true;
			return;
		}

		if (orderProduct) {
			Cart.removeProduct(orderProduct, 1);
		}
	}

	$effect(() => oncountchange?.(count));
</script>

<div class={["flex z-10 pointer-events-none", product.variantType === ProductVariantType.None && "justify-end"]}>
	{#if product.variantType !== ProductVariantType.None && onvariantidchange}
		<Dropdown
			aria-label={translate("variantSelection")}
			class="pointer-events-auto"
			value={product.variant.id}
			onchange={(event: Event & { currentTarget: EventTarget & HTMLSelectElement }): void => {
				onvariantidchange(event.currentTarget.value);
			}}
			name="variants-{product.id}"
			options={product.variants.map((variant) => {
				const stock = getProductStockAmount(product.stock, variant, attributes);

				return {
					value: variant.id,
					label: variant.label[tenant.locale],
					disabled: stock === 0,
					class: stock === 0 ? tw("line-through") : "",
				};
			})}
		/>
		<div class="ml-auto w-1.5"></div>
	{/if}
	<CartQuantityStepper
		{count}
		{disabled}
		{showZeroCount}
		{shrinkOnMobile}
		decrementLabel={translate(count > 1 ? "removeOne" : "removeFromCart")}
		incrementLabel={translate(locked ? "ffcMembersOnly" : count > 0 ? "addAnother" : "addToCart")}
		decrement={(): void => {
			decrement();
		}}
		{increment}
	/>
</div>

<Modal bind:show={showConfirmRemovalModal} heading={translate("confirmRemovalModalTitle")}>
	<div class="mt-4">
		<Formatted text={translate("confirmRemoval", { name: product.name[tenant.locale] })} />
	</div>
	<div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 sm:gap-4 mt-8">
		<Button
			variant={ButtonVariant.Outlined}
			size={ButtonSize.Medium}
			class="w-full sm:w-auto"
			onclick={(): void => {
				showConfirmRemovalModal = false;
			}}
		>
			{translate("keep")}
		</Button>
		<Button
			variant={ButtonVariant.Dark}
			size={ButtonSize.Medium}
			class="w-full sm:w-auto"
			onclick={(): void => {
				showConfirmRemovalModal = false;
				decrement(true);
			}}
		>
			{translate("yesRemove")}
		</Button>
	</div>
</Modal>
